import { useEffect } from "react";
import { Header } from "../components/landing/Header";
import { Footer } from "../components/landing/Footer";

export default function CodeOfConductPage() {
  useEffect(() => {
    const previousTitle = document.title;
    document.title = "Code of Conduct | IQ-Hub";

    const metaDescContent =
      "IQ-Hub's Code of Conduct sets out the standards of behaviour expected of all persons attending our events. Read our full policy here.";
    let metaDesc = document.querySelector('meta[name="description"]');
    const previousDescContent = metaDesc ? metaDesc.getAttribute("content") : null;
    if (!metaDesc) {
      metaDesc = document.createElement("meta");
      metaDesc.setAttribute("name", "description");
      document.head.appendChild(metaDesc);
    }
    metaDesc.setAttribute("content", metaDescContent);

    return () => {
      document.title = previousTitle;
      if (metaDesc && previousDescContent !== null) {
        metaDesc.setAttribute("content", previousDescContent);
      }
    };
  }, []);

  const definitions = [
    {
      term: "Organizer",
      body:
        'means IQ International Pte Ltd (trading as IQ-Hub) or its affiliate conducting the Event.',
    },
    {
      term: "Event",
      body:
        "means any conference, summit, exhibition, or related activity organised or hosted by the Organizer.",
    },
    {
      term: "Attendee",
      body:
        "means any person attending the Event in any capacity, including delegates, speakers, moderators, sponsors, exhibitors, media representatives, Organizer staff, contractors, and accompanying guests.",
    },
    {
      term: "Event Staff",
      body:
        "means personnel authorised by the Organizer to act on its behalf at the Event, including contracted event management personnel and venue staff acting on the Organizer's instructions.",
    },
  ];

  return (
    <div className="min-h-screen bg-[#FAFAF8]" data-testid="code-of-conduct-page">
      <Header />

      <main className="pt-32 pb-20">
        <div className="max-w-4xl mx-auto px-6 lg:px-8">
          <h1
            className="text-4xl lg:text-5xl font-bold text-[#0F172A] mb-6"
            data-testid="coc-h1"
          >
            Code of Conduct
          </h1>

          <div className="prose prose-lg max-w-none text-[#475569]">
            {/* Introductory paragraph */}
            <p className="mb-8" data-testid="coc-intro">
              This Code of Conduct sets out the standards of behaviour expected of
              all persons attending events organised by IQ-Hub. It applies to all
              Attendees in all capacities and forms part of the Event Terms and
              Conditions.
            </p>

            {/* Definitions block */}
            <div className="mb-8" data-testid="coc-definitions">
              <h2 className="text-2xl font-bold text-[#0F172A] mb-4">Definitions</h2>
              {definitions.map((d) => (
                <p key={d.term} className="mb-4">
                  <strong>&ldquo;{d.term}&rdquo;</strong> {d.body}
                </p>
              ))}
            </div>

            {/* Scope paragraph */}
            <p className="mb-10" data-testid="coc-scope">
              This Code applies to all Event-related activities, whether on or off
              venue premises, including official sessions, exhibition areas,
              networking functions, sponsor-hosted activities, and social functions
              associated with the Event.
            </p>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-[#0F172A] mb-4">
                1. Professional Conduct
              </h2>
              <p>
                All Attendees and Event Staff are expected to conduct themselves
                professionally and respectfully at all times. Behaviour by any
                person at the Event that disrupts sessions, exhibitions, or
                networking activities, or that makes Attendees or Event Staff feel
                unsafe or unwelcome, will not be tolerated.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-[#0F172A] mb-4">
                2. Anti-Harassment
              </h2>
              <p>
                The Organizer does not tolerate harassment of any kind &mdash;
                whether verbal, written, physical, or visual &mdash; directed at any
                person at the Event, including Attendees and Event Staff. This
                includes, without limitation: sexual harassment; unwelcome sexual
                attention; stalking; unwanted physical contact; offensive or
                aggressive comments; intimidating behaviour; threats of violence,
                physical harm, or retaliation; and any other conduct intended to
                demean, threaten, or coerce another person at the Event.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-[#0F172A] mb-4">
                3. Non-Discrimination and Inclusion
              </h2>
              <p>
                All persons at the Event are entitled to an inclusive environment.
                Discrimination or harassment on the basis of gender, gender identity
                or expression, sexual orientation, race, ethnicity, religion,
                national origin, age, disability, marital or family status,
                pregnancy, or any other characteristic protected by applicable law
                is strictly prohibited. The Organizer is committed to providing
                reasonable accommodations to Attendees with disabilities; requests
                should be directed to the Organizer in advance of the Event where
                possible.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-[#0F172A] mb-4">
                4. Photography and Recording
              </h2>
              <p className="mb-4">
                The Organizer and its authorised representatives may capture
                photography and video during the Event in public areas &mdash;
                including sessions, exhibition halls, and networking functions
                &mdash; for documentation, marketing, and related purposes.
                Attendees who do not wish to appear in such capture may notify the
                Organizer or the photographer at the Event and reasonable steps will
                be taken to respect the request.
              </p>
              <p className="mb-4">
                Sponsors and exhibitors may photograph and record activity within
                their own designated booth areas and branded stands. Any other
                photography or recording of identifiable individuals by Attendees
                requires the consent of the individual being recorded.
              </p>
              <p>
                Recording of session content &mdash; including presentations, panel
                discussions, and speaker materials &mdash; by Attendees may be
                restricted where communicated by the Organizer or the presenting
                speaker.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-[#0F172A] mb-4">
                5. Confidentiality
              </h2>
              <p>
                Information expressly designated as confidential by a speaker,
                sponsor, or other disclosing party &mdash; whether marked in
                materials or verbally stated at the Event &mdash; must not be
                disclosed outside the Event without the express permission of the
                disclosing party.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-[#0F172A] mb-4">
                6. Compliance with Rules
              </h2>
              <p>
                All Attendees must comply with the reasonable instructions of
                authorised Event Staff, the instructions of the venue, and all
                applicable laws and regulations for the duration of the Event.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-[#0F172A] mb-4">
                7. Health and Safety
              </h2>
              <p>
                Attendees must comply with all health and safety requirements
                communicated by the Organizer and the venue. Any behaviour posing an
                imminent risk to the safety of other Attendees will result in
                removal from the Event.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-[#0F172A] mb-4">
                8. Alcohol and Responsible Conduct
              </h2>
              <p>
                Where alcohol is served at Event functions, Attendees are expected
                to consume responsibly. The Organizer reserves the right to refuse
                further service and to remove Attendees whose conduct, whether or
                not attributable to alcohol, presents a risk to themselves or
                others.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-[#0F172A] mb-4">
                9. Marketing, Solicitation, and Exhibitor Activity
              </h2>
              <p>
                Sponsors and exhibitors may conduct marketing, promotional, and
                solicitation activities within their designated booth areas and such
                other areas as are authorised in writing by the Organizer.
                Unauthorised marketing, solicitation, or distribution of materials
                outside of these designated areas &mdash; by any Attendee &mdash; is
                not permitted.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-[#0F172A] mb-4">
                10. Reporting Violations
              </h2>
              <p className="mb-4">
                Any Attendee who observes, experiences, or becomes aware of conduct
                that may violate this Code is encouraged to report it promptly.
                Reports may be made on-site to any identified member of Event Staff
                or to the Event Director or their designated representative, or in
                writing to the Organizer at{" "}
                <a
                  href="mailto:support@iq-hub.com"
                  className="text-[#C8A951] hover:underline"
                  data-testid="coc-report-email"
                >
                  support@iq-hub.com
                </a>
                .
              </p>
              <p>
                Reports will be handled confidentially to the extent consistent with
                conducting a fair review and taking appropriate action. Retaliation
                against any person who makes a good-faith report, or who
                participates in a review of a report, is strictly prohibited and
                itself constitutes a violation of this Code.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-[#0F172A] mb-4">
                11. Review and Consequences
              </h2>
              <p className="mb-4">
                Reports will be reviewed by representatives designated by the
                Organizer, which may include the Event Director and senior
                leadership. Where a violation is substantiated, consequences may
                include: a verbal or written warning; removal from the Event,
                without refund; revocation of sponsorship, exhibitor, or speaker
                privileges; and referral to the relevant authorities where the
                conduct may constitute a criminal offence.
              </p>
              <p className="mb-4">
                Where the conduct involves an individual representative of a sponsor
                or exhibitor organisation, consequences will ordinarily be applied
                to the individual concerned. The sponsorship or exhibitor status of
                the organisation will not be revoked unless the Organizer determines
                that the organisation failed to take reasonable steps to prevent or
                address the conduct.
              </p>
              <p>
                An Attendee or organisation affected by a decision may submit a
                written appeal to the senior management of the Organizer, at{" "}
                <a
                  href="mailto:support@iq-hub.com"
                  className="text-[#C8A951] hover:underline"
                >
                  support@iq-hub.com
                </a>
                , within ten (10) business days of the decision. The Organizer's
                decision following appeal is final.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-[#0F172A] mb-4">
                12. Governing Provisions
              </h2>
              <p>
                This Code of Conduct forms part of the Event Terms and Conditions
                referenced on the Event website. In the event of any conflict
                between this Code and the Event Terms and Conditions, the Event
                Terms and Conditions shall prevail on matters of contract formation,
                payment, cancellation, and refund, and this Code shall prevail on
                matters of conduct and participation. This Code is governed by the
                law stated in the Event Terms and Conditions.
              </p>
            </section>

            {/* Contact Details Section */}
            <section className="mb-8" data-testid="coc-contact">
              <h2 className="text-2xl font-bold text-[#0F172A] mb-4">
                Contact Details
              </h2>
              <p className="mb-6">
                The channels below are available for all matters relating to this
                Code.
              </p>

              <h3 className="text-lg font-semibold text-[#0F172A] mb-2">
                Reporting a violation
              </h3>
              <p className="mb-2">
                <strong>On-site:</strong> any identified member of Event Staff or
                the Event Director or their designated representative.
              </p>
              <p className="mb-6">
                <strong>In writing:</strong>{" "}
                <a
                  href="mailto:support@iq-hub.com"
                  className="text-[#C8A951] hover:underline"
                >
                  support@iq-hub.com
                </a>
                .
              </p>

              <h3 className="text-lg font-semibold text-[#0F172A] mb-2">
                Escalation
              </h3>
              <p className="mb-6">
                Where a report or appeal concerns a member of Event Staff or the
                Event Director, or where a complainant is not comfortable raising a
                matter through the channels above, reports may be submitted to the
                senior management of the Organizer at{" "}
                <a
                  href="mailto:support@iq-hub.com?subject=Confidential%20%E2%80%94%20For%20the%20attention%20of%20senior%20management"
                  className="text-[#C8A951] hover:underline"
                >
                  support@iq-hub.com
                </a>{" "}
                with &ldquo;Confidential &mdash; For the attention of senior
                management&rdquo; in the subject line.
              </p>

              <h3 className="text-lg font-semibold text-[#0F172A] mb-2">
                Immediate danger
              </h3>
              <p className="mb-6">
                In the event of an immediate risk to safety, contact venue security
                or local emergency services directly and inform Event Staff as soon
                as it is safe to do so.
              </p>

              <h3 className="text-lg font-semibold text-[#0F172A] mb-2">
                General questions about this Code
              </h3>
              <p className="mb-6">
                <a
                  href="mailto:support@iq-hub.com"
                  className="text-[#C8A951] hover:underline"
                >
                  support@iq-hub.com
                </a>
                .
              </p>

              <h3 className="text-lg font-semibold text-[#0F172A] mb-2">
                Organizer
              </h3>
              <p className="mb-1">IQ International Pte Ltd</p>
              <p className="mb-1">
                8 Marina View, #43-01, Asia Square Tower 1, Singapore, 018960
              </p>
              <p>
                <a
                  href="https://www.iq-hub.com"
                  className="text-[#C8A951] hover:underline"
                >
                  www.iq-hub.com
                </a>
              </p>
            </section>

            {/* Version footer */}
            <div
              className="mt-12 pt-8 border-t border-[#E2E8F0]"
              data-testid="coc-version"
            >
              <p className="text-sm text-[#64748B]">
                IQ International Pte Ltd, trading as IQ-Hub | Last updated: February 2026
              </p>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
